import { useEffect, useMemo, useState } from "react";
import { FaFileAlt, FaImage, FaSpinner } from "react-icons/fa";
import "./_Styles/UniversalFilePreview.css";

const MIME_BY_EXTENSION = {
    png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", gif: "image/gif", webp: "image/webp",
    svg: "image/svg+xml", bmp: "image/bmp", avif: "image/avif",
    mp4: "video/mp4", webm: "video/webm", mov: "video/quicktime", m4v: "video/x-m4v",
};

const extensionOf = (file = {}) => {
    const name = String(file.originalName || file.name || file.title || file.url || "");
    const clean = name.split(/[?#]/)[0];
    const dot = clean.lastIndexOf(".");
    return dot >= 0 ? clean.slice(dot + 1).toLowerCase() : "";
};

const mimeOf = (file = {}, blob = null) => {
    const explicit = String(file.mimetype || file.mimeType || blob?.type || "").toLowerCase();
    if (explicit && explicit !== "application/octet-stream") return explicit;
    return MIME_BY_EXTENSION[extensionOf(file)] || explicit;
};

const normalizeBlobMime = (file, blob) => {
    if (!(blob instanceof Blob)) return blob;
    const target = mimeOf(file, blob);
    if (!target || target === blob.type) return blob;
    return new Blob([blob], { type: target });
};

export const PrivateBlobMedia = ({
    file,
    loadBlob,
    className = "",
    alt = "",
    fallback = null,
    video = false,
}) => {
    const [blob, setBlob] = useState(null);
    const [objectUrl, setObjectUrl] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let active = true;
        let nextUrl = "";

        const run = async () => {
            if (!file || typeof loadBlob !== "function") {
                setLoading(false);
                setError(true);
                return;
            }

            setLoading(true);
            setError(false);
            setBlob(null);
            setObjectUrl("");

            try {
                let nextBlob = await loadBlob(file);
                if (!(nextBlob instanceof Blob)) throw new Error("Archivo invalido");
                nextBlob = normalizeBlobMime(file, nextBlob);
                if (!active) return;
                nextUrl = URL.createObjectURL(nextBlob);
                setBlob(nextBlob);
                setObjectUrl(nextUrl);
            } catch {
                if (active) setError(true);
            } finally {
                if (active) setLoading(false);
            }
        };

        run();

        return () => {
            active = false;
            if (nextUrl) URL.revokeObjectURL(nextUrl);
        };
    }, [file?.id, file?.updatedAt, loadBlob]);

    const isVideo = useMemo(() => video || mimeOf(file, blob).startsWith("video/"), [video, file, blob]);

    if (loading) {
        return <span className={`LStudio__private_blob_media is-loading ${className}`} aria-label="Cargando vista previa">
            <FaSpinner />
        </span>;
    }

    if (error || !objectUrl) {
        return <span className={`LStudio__private_blob_media is-error ${className}`}>
            {fallback || (String(file?.kind || "").toUpperCase() === "IMAGE" ? <FaImage /> : <FaFileAlt />)}
        </span>;
    }

    if (isVideo) {
        return <video className={`LStudio__private_blob_media is-video ${className}`} src={objectUrl} muted preload="metadata" />;
    }

    return <img className={`LStudio__private_blob_media is-image ${className}`} src={objectUrl} alt={alt || file?.originalName || file?.title || "Archivo"} loading="lazy" />;
};
